import type { PhotoId } from '../gallery/photos';
import type { Fill, TextLayer } from '../template';
import {
  lin,
  solid,
  step,
  t,
  type Box,
  type SceneRef,
  type SceneSpec,
  type Tx,
  type VideoBuilder,
} from './kit';

/**
 * Motion reels: launch films, showreels, kinetic manifestos, data stories and brand films in
 * the style of modern motion design (viewfinder HUD, terminal prompts, code, kinetic words,
 * particles, ribbons). A style is a blueprint that lays out scenes from a short piece of copy
 * and a palette, so the gallery and the AI build from the same code.
 */

export const REEL_STYLE_IDS = ['launch', 'showreel', 'manifesto', 'data', 'brand'] as const;
export type ReelStyleId = (typeof REEL_STYLE_IDS)[number];

/** Short copy of a reel; every field has a sensible use in every style. */
export interface ReelCopy {
  /** Brand or product name. */
  brand: string;
  /** Viewfinder label ("LAUNCH FILM 2026"). */
  hud: string;
  /** What gets typed in the terminal ("make me a launch video"). */
  prompt: string;
  /** 2-5 punchy words, one per beat ("Prompt.", "Motion.", "Ship."). */
  words: string[];
  /** The main line ("Every frame on purpose"). */
  headline: string;
  /** One short sentence under the brand. */
  tagline: string;
  /** Call to action. */
  cta: string;
  /** 3-6 topics or features. */
  topics: string[];
  stat: { value: number; prefix?: string; suffix?: string; label: string };
  /** A few lines of code for the editor scene. */
  code: string[];
  photo?: PhotoId;
}

export interface ReelPalette {
  bg: string;
  ink: string;
  accent: string;
  accent2: string;
  /** A third colour for single scenes (yellow, lime…). */
  pop: string;
  paper: string;
  paperInk: string;
  font: string;
  weight: number;
  upper: boolean;
  serif: string;
}

export const REEL_PALETTES = {
  ember: {
    bg: '#0D0D10',
    ink: '#F4F2EE',
    accent: '#FF4A0D',
    accent2: '#2B3BFF',
    pop: '#FFC93C',
    paper: '#EFEBE4',
    paperInk: '#111114',
    font: 'Inter',
    weight: 900,
    upper: true,
    serif: 'Playfair Display',
  },
  signal: {
    bg: '#111014',
    ink: '#F5F3EF',
    accent: '#EF3B33',
    accent2: '#2D3BF0',
    pop: '#F2C230',
    paper: '#ECE8E1',
    paperInk: '#121212',
    font: 'Montserrat',
    weight: 900,
    upper: true,
    serif: 'Playfair Display',
  },
  cobalt: {
    bg: '#081033',
    ink: '#EEF2FF',
    accent: '#3D5AFE',
    accent2: '#00E5A0',
    pop: '#FFD166',
    paper: '#EEF1FF',
    paperInk: '#0A1238',
    font: 'Unbounded',
    weight: 800,
    upper: false,
    serif: 'Lora',
  },
  acid: {
    bg: '#0B0B0B',
    ink: '#F4F4F0',
    accent: '#C6FF3D',
    accent2: '#FF3DA5',
    pop: '#3DD9FF',
    paper: '#F4F4F0',
    paperInk: '#0B0B0B',
    font: 'Inter Tight',
    weight: 900,
    upper: true,
    serif: 'Playfair Display',
  },
  mono: {
    bg: '#0A0A0A',
    ink: '#F5F5F5',
    accent: '#F5F5F5',
    accent2: '#FF3B30',
    pop: '#FF3B30',
    paper: '#EDEDED',
    paperInk: '#0A0A0A',
    font: 'Manrope',
    weight: 800,
    upper: true,
    serif: 'Playfair Display',
  },
  sunset: {
    bg: '#1A0F1F',
    ink: '#FFF3E8',
    accent: '#FF7A59',
    accent2: '#FFC24B',
    pop: '#FF3D7F',
    paper: '#FFF3E8',
    paperInk: '#2A1420',
    font: 'Rubik',
    weight: 800,
    upper: false,
    serif: 'Lora',
  },
  mint: {
    bg: '#0D1B16',
    ink: '#EAF7F1',
    accent: '#35E0A1',
    accent2: '#FFD166',
    pop: '#7C5CFF',
    paper: '#EAF7F1',
    paperInk: '#0D1B16',
    font: 'Golos Text',
    weight: 800,
    upper: false,
    serif: 'Lora',
  },
} satisfies Record<string, ReelPalette>;
export type ReelPaletteId = keyof typeof REEL_PALETTES;
export const REEL_PALETTE_IDS = Object.keys(REEL_PALETTES) as ReelPaletteId[];

/** Fonts a reel may use for display type (all built in, all with Cyrillic). */
export const REEL_FONTS = [
  'Inter',
  'Inter Tight',
  'Montserrat',
  'Unbounded',
  'Manrope',
  'Rubik',
  'Golos Text',
  'Oswald',
  'Russo One',
  'Playfair Display',
];

const MONO = 'JetBrains Mono';

const SN = {
  prompt: t('Prompt', 'Запит', 'Запрос', 'Polecenie', 'Prompt', 'Prompt'),
  logo: t('Logo', 'Логотип', 'Логотип', 'Logo', 'Logo', 'Logo'),
  words: t(
    'Kinetic words',
    'Кінетичні слова',
    'Кинетические слова',
    'Kinetyczne słowa',
    'Cuvinte cinetice',
    'Palabras cinéticas',
  ),
  slice: t('Title', 'Заголовок', 'Заголовок', 'Tytuł', 'Titlu', 'Título'),
  code: t('Code', 'Код', 'Код', 'Kod', 'Cod', 'Código'),
  product: t('Product', 'Продукт', 'Продукт', 'Produkt', 'Produs', 'Producto'),
  cta: t('Call to action', 'Заклик', 'Призыв', 'Wezwanie', 'Îndemn', 'Llamada'),
  intro: t('Intro', 'Вступ', 'Вступление', 'Intro', 'Intro', 'Intro'),
  statement: t('Statement', 'Заява', 'Заявление', 'Deklaracja', 'Declarație', 'Declaración'),
  grid: t('Dot grid', 'Сітка точок', 'Сетка точек', 'Siatka kropek', 'Grilă de puncte', 'Rejilla'),
  particles: t('Particles', 'Частинки', 'Частицы', 'Cząsteczki', 'Particule', 'Partículas'),
  ribbon: t('Ribbon', 'Стрічка', 'Лента', 'Wstęga', 'Panglică', 'Cinta'),
  topics: t('Topics', 'Теми', 'Темы', 'Tematy', 'Teme', 'Temas'),
  name: t('Signature', 'Підпис', 'Подпись', 'Podpis', 'Semnătură', 'Firma'),
  wall: t(
    'Word wall',
    'Стіна слів',
    'Стена слов',
    'Ściana słów',
    'Zid de cuvinte',
    'Muro de palabras',
  ),
  stack: t('Stack', 'Стовпчик', 'Столбик', 'Stos', 'Stivă', 'Pila'),
  hook: t('Hook', 'Гачок', 'Зацепка', 'Haczyk', 'Cârlig', 'Gancho'),
  stat: t('Number', 'Число', 'Число', 'Liczba', 'Număr', 'Número'),
  chart: t('Chart', 'Графік', 'График', 'Wykres', 'Grafic', 'Gráfico'),
  board: t('Board', 'Табло', 'Табло', 'Tablica', 'Panou', 'Panel'),
  open: t('Opening', 'Відкриття', 'Открытие', 'Otwarcie', 'Deschidere', 'Apertura'),
  line: t('Line', 'Рядок', 'Строка', 'Linia', 'Rând', 'Línea'),
  end: t('End card', 'Фінал', 'Финал', 'Zakończenie', 'Final', 'Cierre'),
};

/** Display type in the palette's font. */
function display(
  p: ReelPalette,
  size: number,
  color: string,
  extra: Partial<TextLayer> = {},
): Partial<TextLayer> {
  return {
    fontFamily: p.font,
    fontWeight: p.weight,
    fontSize: size,
    color,
    lineHeight: 0.95,
    letterSpacing: -Math.round(size * 0.035),
    textTransform: p.upper ? 'uppercase' : 'none',
    ...extra,
  };
}

/** Small monospaced captions. */
function mono(size: number, color: string, extra: Partial<TextLayer> = {}): Partial<TextLayer> {
  return {
    fontFamily: MONO,
    fontWeight: 500,
    fontSize: size,
    color,
    lineHeight: 1.2,
    letterSpacing: Math.round(size * 0.12),
    textTransform: 'uppercase',
    autoFit: true,
    maxLines: 1,
    ...extra,
  };
}

/** The viewfinder over one scene, its timecode continuing from the scene before. */
function hud(b: VideoBuilder, s: SceneRef, c: ReelCopy, color: string, accent: string) {
  b.addFx(s, 'hud', 0, s.duration, {
    label: c.hud,
    label2: 'REC',
    label3: c.brand,
    color,
    color2: accent,
    from: s.start,
    inset: b.tall ? 5 : 3,
    opacity: 0.8,
  });
}

const words = (c: ReelCopy) => c.words.filter(Boolean).slice(0, 5);

// ---------------------------------------------------------------- launch film

function launchScenes(c: ReelCopy): SceneSpec[] {
  const n = Math.max(2, words(c).length);
  return [
    {
      key: 'prompt',
      name: SN.prompt,
      duration: 3400,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'logo', name: SN.logo, duration: 2000, transition: { preset: 'cut' } },
    {
      key: 'words',
      name: SN.words,
      duration: Math.max(1800, n * 620),
      transition: { preset: 'cut' },
    },
    {
      key: 'slice',
      name: SN.slice,
      duration: 1800,
      transition: { preset: 'glitch', duration: 400 },
    },
    {
      key: 'code',
      name: SN.code,
      duration: 3800,
      transition: { preset: 'push', duration: 600, params: { direction: 'up' } },
    },
    {
      key: 'product',
      name: SN.product,
      duration: 2800,
      transition: { preset: 'zoom-out', duration: 600 },
    },
    { key: 'cta', name: SN.cta, duration: 3000 },
  ];
}

/** A mark of three rounded tiles (the brand's logo stand-in). */
function mark(b: VideoBuilder, s: SceneRef, box: Box, color: string) {
  const k = box.w / 3;
  const tiles: Array<[number, number, number]> = [
    [0, 0, 0],
    [1, 1, 120],
    [0, 2, 240],
  ];
  for (const [cx, cy, delay] of tiles)
    b.shape(
      s,
      'mark_tile',
      { x: box.x + cx * k * 1.05, y: box.y + (cy * k * 0.95) / 1.05, w: k * 1.9, h: k * 0.9 },
      { fill: solid(color), borderRadius: Math.round(k * 0.22) },
      { enter: step('pop', delay, 520, 'spring(180, 14)') },
    );
}

function launchBuild(b: VideoBuilder, c: ReelCopy, p: ReelPalette): Fill {
  b.poster = 'product';
  const F = b.F;
  const s0 = b.sc('prompt');
  b.backdrop(s0, 'prompt_bg', solid(p.bg));
  hud(b, s0, c, p.ink, p.accent);
  // The prompt is a text layer (replaceable through the API); the terminal types it
  b.text(
    s0,
    'prompt',
    c.prompt,
    b.in(0.04, 0.46, 0.92, 0.08),
    mono(b.s(0.032), p.ink, { letterSpacing: 0, textTransform: 'none' }),
    {
      effect: {
        kind: 'terminal',
        delay: 200,
        params: { color: p.ink, color2: p.accent, size: b.tall ? 3.6 : 3.2 },
      },
    },
  );

  const s1 = b.sc('logo');
  b.backdrop(s1, 'logo_bg', solid(p.bg));
  const m = b.s(b.wide ? 0.16 : 0.2);
  const row = !b.upright;
  const markBox = row
    ? { x: b.cx - m * 1.9, y: b.cy - m / 2, w: m * 0.6, h: m }
    : { x: b.cx - m * 0.4, y: F.y + F.h * 0.3, w: m * 0.6, h: m };
  mark(b, s1, markBox, p.accent);
  b.text(
    s1,
    'brand',
    c.brand,
    row
      ? { x: b.cx - m * 0.6, y: b.cy - m * 0.55, w: F.x + F.w - (b.cx - m * 0.6), h: m * 1.1 }
      : b.in(0, 0.48, 1, 0.16),
    display(p, b.s(0.14), p.ink, { align: row ? 'left' : 'center', textTransform: 'none' }),
    { enter: step('mask-up', 250, 600), split: { by: 'char', stagger: 30 } },
  );
  b.text(
    s1,
    'logo_tagline',
    c.tagline,
    row ? b.in(0.1, 0.66, 0.8, 0.06) : b.in(0, 0.66, 1, 0.06),
    mono(b.s(0.022), p.ink, { align: 'center', color: `${p.ink}AA` }),
    {
      enter: step('fade', 900, 500),
    },
  );

  const s2 = b.sc('words');
  b.backdrop(s2, 'words_bg', solid(p.accent));
  b.addFx(s2, 'words', 0, s2.duration, {
    text: words(c).join('\n'),
    style: 'slam',
    bg: 'flip',
    color: p.ink,
    color2: p.accent,
    color3: p.bg,
    font: p.font,
    weight: String(p.weight),
  });

  const s3 = b.sc('slice');
  b.backdrop(s3, 'slice_bg', solid(p.accent));
  b.text(
    s3,
    'slice_word',
    words(c)[words(c).length - 1] ?? c.brand,
    b.in(0, 0.3, 1, 0.4),
    display(p, b.s(0.2), p.bg, { maxLines: 1, autoFit: true }),
    {
      effect: { kind: 'slice-text', delay: 0, params: { count: 8, color2: p.bg, mode: 'in-out' } },
    },
  );

  const s4 = b.sc('code');
  b.backdrop(s4, 'code_bg', solid(p.bg));
  hud(b, s4, c, p.ink, p.accent);
  b.addFx(s4, 'code', 200, s4.duration - 200, {
    text: c.code.join('\n'),
    title: `${c.brand.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'launch'}.tsx`,
    tilt: 'tilt',
    size: b.tall ? 2.8 : 3.4,
    bg: '#16161C',
    color2: p.accent,
    color3: p.accent2 === p.accent ? p.pop : p.accent2,
  });
  const codeIn = b.upright ? b.in(0, 0.84, 1, 0.12) : b.in(0, 0.78, 0.6, 0.16);
  b.text(
    s4,
    'code_caption',
    c.headline,
    codeIn,
    display(p, b.s(0.08), p.ink, {
      align: b.upright ? 'center' : 'left',
      textTransform: 'none',
      maxLines: 2,
    }),
    {
      enter: step('rise', 500, 600),
    },
  );

  const s5 = b.sc('product');
  b.backdrop(s5, 'product_bg', solid(p.paper));
  const card = b.upright ? b.in(0.02, 0.16, 0.96, 0.5) : b.in(0.04, 0.08, 0.92, 0.76);
  b.shape(
    s5,
    'product_card',
    card,
    { fill: solid(p.accent), borderRadius: b.s(0.03) },
    { enter: step('zoom-out', 0, 700), keys: b.kenBurns(1, 1.04) },
  );
  const big = b.text(
    s5,
    'product_word',
    words(c)[0] ?? c.brand,
    { x: card.x + card.w * 0.06, y: card.y + card.h * 0.2, w: card.w * 0.88, h: card.h * 0.6 },
    display(p, b.s(0.3), p.paper, { maxLines: 1 }),
    {
      enter: step('zoom-out', 150, 700),
    },
  );
  b.addFx(s5, 'brackets', 300, s5.duration - 300, {
    target: big.id,
    color: p.paperInk,
    label: c.hud,
  });
  b.chip(
    s5,
    'product_chip',
    c.tagline,
    { x: b.cx, y: card.y + card.h + b.s(0.04), h: b.s(0.07), align: 'center' },
    { bg: p.paperInk, color: p.paper, font: MONO, weight: 500, size: b.s(0.026) },
    {
      enter: step('rise', 600, 500),
    },
  );

  const s6 = b.sc('cta');
  b.backdrop(s6, 'cta_bg', solid(p.bg));
  hud(b, s6, c, p.ink, p.accent);
  const head = b.text(
    s6,
    'cta_headline',
    c.cta,
    b.in(0, 0.28, 1, 0.22),
    display(p, b.s(0.14), p.accent, { maxLines: 2 }),
    {
      enter: step('zoom-out', 0, 600),
      emphasis: { preset: 'pulse', delay: 1200, duration: 900, repeat: 1, gap: 0 },
    },
  );
  b.addFx(s6, 'underline', 700, 900, { target: head.id, color: p.accent });
  b.text(
    s6,
    'cta_brand',
    c.brand,
    b.in(0, 0.56, 1, 0.12),
    display(p, b.s(0.08), p.ink, { textTransform: 'none' }),
    { enter: step('mask-up', 400, 600) },
  );
  b.text(
    s6,
    'cta_tagline',
    c.tagline,
    b.in(0, 0.72, 1, 0.06),
    mono(b.s(0.022), p.ink, { color: `${p.ink}AA`, align: 'center' }),
    { enter: step('typewriter', 900, 1000) },
  );
  return solid(p.bg);
}

// ---------------------------------------------------------------- showreel

function showreelScenes(): SceneSpec[] {
  return [
    {
      key: 'intro',
      name: SN.intro,
      duration: 2400,
      transition: { preset: 'circle', duration: 600 },
    },
    {
      key: 'statement',
      name: SN.statement,
      duration: 2800,
      transition: { preset: 'whip-pan', duration: 450 },
    },
    { key: 'grid', name: SN.grid, duration: 2400, transition: { preset: 'slices', duration: 600 } },
    {
      key: 'particles',
      name: SN.particles,
      duration: 2600,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    {
      key: 'ribbon',
      name: SN.ribbon,
      duration: 2400,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'topics',
      name: SN.topics,
      duration: 3000,
      transition: { preset: 'iris', duration: 600 },
    },
    { key: 'name', name: SN.name, duration: 3200 },
  ];
}

function showreelBuild(b: VideoBuilder, c: ReelCopy, p: ReelPalette): Fill {
  b.poster = 'statement';
  const s0 = b.sc('intro');
  b.backdrop(s0, 'intro_bg', solid(p.bg));
  hud(b, s0, c, p.ink, p.accent);
  b.addFx(s0, 'orbit', 0, s0.duration, {
    shape: 'pills',
    count: 6,
    size: 18,
    turns: 0.4,
    color: p.ink,
    color2: p.accent,
  });
  b.text(
    s0,
    'intro_label',
    c.hud,
    b.in(0, 0.82, 1, 0.05),
    mono(b.s(0.022), p.ink, { align: 'center', color: `${p.ink}99` }),
    { enter: step('fade', 600, 500) },
  );

  const s1 = b.sc('statement');
  b.backdrop(s1, 'statement_bg', solid(p.accent));
  const statement = b.text(
    s1,
    'statement',
    c.headline,
    b.in(0.02, 0.3, 0.96, 0.4),
    display(p, b.s(0.13), p.bg, { maxLines: 3 }),
    {
      enter: step('letters-blur', 100, 700),
      split: { by: 'char', stagger: 28, order: 'start' },
    },
  );
  b.addFx(s1, 'brackets', 900, s1.duration - 900, { target: statement.id, color: p.bg, pad: 6 });

  const s2 = b.sc('grid');
  b.backdrop(s2, 'grid_bg', solid(p.paper));
  b.addFx(s2, 'dot-grid', 0, s2.duration, {
    style: 'plane',
    count: 20,
    color: p.paperInk,
    color2: p.accent,
    opacity: 0.9,
  });
  b.text(
    s2,
    'grid_topic',
    c.topics[0] ?? c.brand,
    b.in(0, 0.12, 1, 0.12),
    display(p, b.s(0.09), p.paperInk),
    { enter: step('words-rise', 200, 600), split: { by: 'word', stagger: 80 } },
  );

  const s3 = b.sc('particles');
  b.backdrop(s3, 'particles_bg', solid(p.accent2));
  b.addFx(s3, 'particles-3d', 0, s3.duration, {
    shape: 'sphere',
    count: 260,
    size: b.wide ? 30 : 26,
    y: 46,
    turns: 0.5,
    color: '#FFFFFF',
    color2: p.pop,
  });
  b.text(
    s3,
    'particles_topic',
    c.topics[1] ?? c.brand,
    b.in(0, 0.9, 1, 0.06),
    mono(b.s(0.026), '#FFFFFF', { align: 'center' }),
    { enter: step('fade', 400, 500) },
  );

  const s4 = b.sc('ribbon');
  b.backdrop(s4, 'ribbon_bg', solid(p.pop));
  b.addFx(s4, 'ribbon', 0, s4.duration, {
    shape: 'loop',
    draw: 'draw',
    width: 7,
    color: p.bg,
    color2: '#FFFFFF',
    color3: p.accent2,
    color4: p.accent,
  });
  b.text(
    s4,
    'ribbon_topic',
    c.topics[2] ?? c.brand,
    b.in(0, 0.06, 1, 0.1),
    display(p, b.s(0.07), p.bg),
    { enter: step('rise', 600, 500) },
  );

  const s5 = b.sc('topics');
  b.backdrop(s5, 'topics_bg', solid(p.bg));
  b.addFx(s5, 'text-rings', 0, s5.duration, {
    text: c.topics.join('\n'),
    count: 3,
    size: b.wide ? 44 : 40,
    color: p.ink,
    color2: p.accent,
    turns: 0.3,
    font: p.font,
  });

  const s6 = b.sc('name');
  b.backdrop(s6, 'name_bg', solid(p.paper));
  b.text(
    s6,
    'name',
    c.brand,
    b.in(0, 0.32, 1, 0.22),
    {
      fontFamily: p.serif,
      fontWeight: 700,
      fontSize: b.s(0.17),
      color: p.paperInk,
      lineHeight: 1,
      maxLines: 1,
    },
    {
      enter: step('letters-drop', 100, 600),
      split: { by: 'char', stagger: 45 },
    },
  );
  // The full stop after the name, like a signature
  const nameW = Math.min(b.F.w, b.textW(c.brand, b.s(0.17), 0.52));
  const dot = b.s(0.03);
  b.shape(
    s6,
    'name_dot',
    b.centered(
      Math.min(b.F.x + b.F.w - dot, b.cx + nameW / 2 + dot),
      b.F.y + b.F.h * 0.32 + b.F.h * 0.22 * 0.72,
      dot,
      dot,
    ),
    { shape: 'ellipse', fill: solid(p.accent) },
    { enter: step('pop', 900, 500) },
  );
  b.text(
    s6,
    'role',
    c.tagline,
    b.in(0, 0.6, 1, 0.06),
    mono(b.s(0.024), p.paperInk, { align: 'center', letterSpacing: Math.round(b.s(0.024) * 0.3) }),
    { enter: step('fade', 1100, 600) },
  );
  b.text(
    s6,
    'name_cta',
    c.cta,
    b.in(0, 0.7, 1, 0.05),
    mono(b.s(0.02), p.paperInk, { align: 'center', color: `${p.paperInk}99` }),
    { enter: step('typewriter', 1400, 800) },
  );
  return solid(p.bg);
}

// ---------------------------------------------------------------- manifesto

function manifestoScenes(c: ReelCopy): SceneSpec[] {
  const n = Math.max(2, words(c).length);
  return [
    {
      key: 'words',
      name: SN.words,
      duration: Math.max(2100, n * 700),
      transition: { preset: 'cut' },
    },
    {
      key: 'wall',
      name: SN.wall,
      duration: 2800,
      transition: { preset: 'stripes', duration: 600 },
    },
    {
      key: 'stack',
      name: SN.stack,
      duration: 2800,
      transition: { preset: 'flash', duration: 300 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ];
}

function manifestoBuild(b: VideoBuilder, c: ReelCopy, p: ReelPalette): Fill {
  b.poster = 'cta';
  const s0 = b.sc('words');
  b.backdrop(s0, 'words_bg', solid(p.bg));
  b.addFx(s0, 'words', 0, s0.duration, {
    text: words(c).join('\n'),
    style: 'slam',
    bg: 'flip',
    color: p.ink,
    color2: p.accent,
    color3: p.bg,
    font: p.font,
    weight: String(p.weight),
  });

  const s1 = b.sc('wall');
  b.backdrop(s1, 'wall_bg', solid(p.paper));
  b.addFx(s1, 'word-wall', 0, s1.duration, {
    text: words(c)[0] ?? c.brand,
    count: 7,
    tilt: -8,
    color: p.paperInk,
    color2: p.accent,
    opacity: 0.18,
    font: p.font,
  });
  b.text(
    s1,
    'headline',
    c.headline,
    b.in(0.04, 0.3, 0.92, 0.4),
    display(p, b.s(0.12), p.paperInk, { maxLines: 3 }),
    {
      effect: {
        kind: 'extrude',
        delay: 0,
        params: { count: 14, angle: 135, size: 30, color2: p.accent },
      },
    },
  );

  const s2 = b.sc('stack');
  b.backdrop(s2, 'stack_bg', solid(p.accent));
  b.addFx(s2, 'words', 0, s2.duration, {
    text: c.topics.slice(0, 4).join('\n'),
    style: 'stack',
    bg: 'none',
    color: p.bg,
    font: p.font,
    weight: String(p.weight),
  });

  const s3 = b.sc('cta');
  b.backdrop(s3, 'cta_bg', solid(p.bg));
  b.text(
    s3,
    'brand',
    c.brand,
    b.in(0, 0.32, 1, 0.18),
    display(p, b.s(0.14), p.ink, { textTransform: 'none' }),
    { enter: step('mask-up', 0, 600), split: { by: 'char', stagger: 30 } },
  );
  b.chip(
    s3,
    'cta',
    c.cta,
    { x: b.cx, y: b.F.y + b.F.h * 0.58, h: b.s(0.09), align: 'center' },
    { bg: p.accent, color: p.bg, font: p.font, weight: p.weight, size: b.s(0.04) },
    {
      enter: step('pop', 600, 500),
      emphasis: { preset: 'pulse', delay: 1400, duration: 900, repeat: 1, gap: 0 },
    },
  );
  b.addFx(s3, 'sticker', 400, s3.duration - 400, {
    ring: `${c.brand} • ${c.cta} • `,
    label:
      c.stat.prefix || c.stat.suffix
        ? `${c.stat.prefix ?? ''}${c.stat.value}${c.stat.suffix ?? ''}`
        : '★',
    size: b.tall ? 14 : 12,
    x: b.wide ? 82 : 76,
    y: b.tall ? 24 : 22,
    color: p.pop,
    color2: p.bg,
  });
  return solid(p.bg);
}

// ---------------------------------------------------------------- data story

function dataScenes(): SceneSpec[] {
  return [
    {
      key: 'hook',
      name: SN.hook,
      duration: 2600,
      transition: { preset: 'push', duration: 500, params: { direction: 'up' } },
    },
    { key: 'stat', name: SN.stat, duration: 3000, transition: { preset: 'panels', duration: 700 } },
    { key: 'chart', name: SN.chart, duration: 3200, transition: { preset: 'wipe', duration: 500 } },
    {
      key: 'board',
      name: SN.board,
      duration: 2600,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ];
}

function dataBuild(b: VideoBuilder, c: ReelCopy, p: ReelPalette): Fill {
  b.poster = 'hook';
  const s0 = b.sc('hook');
  b.backdrop(s0, 'hook_bg', solid(p.paper));
  hud(b, s0, c, p.paperInk, p.accent);
  b.text(
    s0,
    'hook_kicker',
    c.hud,
    b.in(0, 0.26, 1, 0.05),
    mono(b.s(0.024), p.accent, { align: 'center' }),
    { enter: step('fade', 100, 400) },
  );
  b.text(
    s0,
    'hook',
    c.headline,
    b.in(0.02, 0.34, 0.96, 0.32),
    display(p, b.s(0.11), p.paperInk, { maxLines: 3 }),
    {
      enter: step('lines-mask', 200, 700),
      split: { by: 'line', stagger: 140 },
    },
  );

  const s1 = b.sc('stat');
  b.backdrop(s1, 'stat_bg', solid(p.pop));
  const side = !b.upright;
  b.addFx(s1, 'counter', 0, s1.duration, {
    from: 0,
    to: Math.max(0, Math.round(c.stat.value)),
    prefix: c.stat.prefix ?? '',
    suffix: c.stat.suffix ?? '',
    size: side ? 15 : 22,
    x: side ? 29 : 50,
    y: side ? 50 : 32,
    color: p.paperInk,
    font: p.font,
    weight: String(p.weight),
  });
  b.addFx(s1, 'donut', 300, s1.duration - 300, {
    value: Math.max(0, Math.min(100, Math.round(c.stat.value))),
    size: side ? 15 : 16,
    x: side ? 74 : 50,
    y: side ? 50 : 70,
    color: p.paperInk,
    color2: p.accent,
    font: p.font,
  });
  b.text(
    s1,
    'stat_label',
    c.stat.label,
    b.in(0, 0.92, 1, 0.05),
    mono(b.s(0.022), p.paperInk, { align: 'center' }),
    { enter: step('fade', 900, 400) },
  );

  const s2 = b.sc('chart');
  b.backdrop(s2, 'chart_bg', solid(p.bg));
  b.addFx(s2, 'bars', 0, s2.duration, {
    values: '3, 4, 5, 4, 6, 7, 9',
    size: b.upright ? 80 : 40,
    height: b.upright ? 22 : 40,
    x: b.upright ? 50 : 28,
    y: b.upright ? 34 : 55,
    color: p.ink,
    color2: p.accent,
    label: c.topics[0] ?? '',
  });
  b.addFx(s2, 'line-chart', 400, s2.duration - 400, {
    values: '2, 3, 2.6, 4, 3.8, 5.4, 5, 7.2',
    size: b.upright ? 80 : 40,
    height: b.upright ? 22 : 40,
    x: b.upright ? 50 : 72,
    y: b.upright ? 70 : 55,
    color: p.ink,
    color2: p.accent2,
  });
  b.text(
    s2,
    'chart_title',
    c.topics[1] ?? c.headline,
    b.in(0, 0.02, 1, 0.08),
    display(p, b.s(0.06), p.ink, { maxLines: 1 }),
    { enter: step('rise', 200, 500) },
  );

  const s3 = b.sc('board');
  b.backdrop(s3, 'board_bg', solid(p.bg));
  b.addFx(s3, 'split-flap', 0, s3.duration, {
    text: words(c).slice(0, 2).join('\n'),
    size: b.tall ? 7 : 8,
    color: p.ink,
    bg: '#1C1C22',
    color2: p.accent,
  });
  b.text(
    s3,
    'board_caption',
    c.tagline,
    b.in(0, 0.84, 1, 0.06),
    mono(b.s(0.022), p.ink, { align: 'center', color: `${p.ink}AA` }),
    { enter: step('fade', 1600, 500) },
  );

  const s4 = b.sc('cta');
  b.backdrop(s4, 'cta_bg', solid(p.accent));
  b.text(
    s4,
    'brand',
    c.brand,
    b.in(0, 0.28, 1, 0.18),
    display(p, b.s(0.13), p.bg, { textTransform: 'none' }),
    { enter: step('zoom-out', 0, 600) },
  );
  b.chip(
    s4,
    'cta',
    c.cta,
    { x: b.cx, y: b.F.y + b.F.h * 0.52, h: b.s(0.085), align: 'center' },
    { bg: p.bg, color: p.accent, font: p.font, weight: p.weight, size: b.s(0.036) },
    { enter: step('pop', 500, 500) },
  );
  b.addFx(s4, 'ticker', 300, s4.duration - 300, {
    text: c.topics.join('\n'),
    y: b.tall ? 82 : 88,
    bg: p.bg,
    color: p.ink,
    color2: p.pop,
    color3: p.accent2,
  });
  return solid(p.bg);
}

// ---------------------------------------------------------------- brand film

function brandScenes(): SceneSpec[] {
  return [
    { key: 'open', name: SN.open, duration: 3400, transition: { preset: 'blur', duration: 700 } },
    {
      key: 'line',
      name: SN.line,
      duration: 3000,
      transition: { preset: 'fade-through', duration: 600 },
    },
    {
      key: 'words',
      name: SN.words,
      duration: 2400,
      transition: { preset: 'crossfade', duration: 700 },
    },
    { key: 'end', name: SN.end, duration: 3000 },
  ];
}

function brandBuild(b: VideoBuilder, c: ReelCopy, p: ReelPalette): Fill {
  b.poster = 'open';
  const photo: PhotoId = c.photo ?? 'mountain-lake';
  const s0 = b.sc('open');
  b.photo(s0, 'photo', photo, b.full(), {}, { keys: b.kenBurns(1.18, 1.04) });
  b.shade(s0, 'shade', b.full(), [
    [0, '#00000066'],
    [0.5, '#00000022'],
    [1, '#000000AA'],
  ]);
  hud(b, s0, c, '#FFFFFF', p.accent);
  b.text(
    s0,
    'brand',
    c.brand.toUpperCase(),
    b.in(0, 0.4, 1, 0.16),
    display(p, b.s(0.12), '#FFFFFF', { letterSpacing: Math.round(b.s(0.12) * 0.3), maxLines: 1 }),
    {
      enter: step('letters-blur', 400, 900),
      split: { by: 'char', stagger: 60, order: 'center' },
    },
  );
  b.addFx(s0, 'light-leak', 0, s0.duration, {
    count: 2,
    color: p.accent,
    color2: p.pop,
    color3: '#FFFFFF',
    opacity: 0.45,
  });

  const s1 = b.sc('line');
  b.photo(
    s1,
    'photo_line',
    photo,
    b.full(),
    { focusX: 0.3 },
    { keys: b.kenBurns(1.08, 1.22, 0.02, -0.01) },
  );
  b.shade(s1, 'shade_line', b.full(), [
    [0, '#00000000'],
    [0.45, '#00000033'],
    [1, '#000000CC'],
  ]);
  b.text(
    s1,
    'line_kicker',
    c.hud,
    b.in(0, 0.62, 1, 0.05),
    mono(b.s(0.022), '#FFFFFFCC', { align: b.upright ? 'center' : 'left' }),
    { enter: step('fade', 200, 500) },
  );
  b.text(
    s1,
    'line',
    c.headline,
    b.in(0, 0.68, 1, 0.24),
    display(p, b.s(0.09), '#FFFFFF', {
      maxLines: 2,
      align: b.upright ? 'center' : 'left',
      textTransform: 'none',
    }),
    {
      enter: step('lines-mask', 400, 800),
      split: { by: 'line', stagger: 160 },
    },
  );

  const s2 = b.sc('words');
  b.photo(
    s2,
    'photo_words',
    photo,
    b.full(),
    { focusX: 0.7, blur: 6 },
    { keys: b.kenBurns(1.2, 1.1) },
  );
  b.shade(s2, 'shade_words', b.full(), [
    [0, '#000000AA'],
    [1, '#000000AA'],
  ]);
  b.addFx(s2, 'words', 0, s2.duration, {
    text: words(c).join('\n'),
    style: 'blur',
    bg: 'none',
    color: '#FFFFFF',
    font: p.font,
    weight: String(p.weight),
  });

  const s3 = b.sc('end');
  b.backdrop(s3, 'end_bg', lin(160, p.bg, '#000000'));
  b.text(
    s3,
    'end_brand',
    c.brand,
    b.in(0, 0.34, 1, 0.16),
    display(p, b.s(0.12), p.ink, { textTransform: 'none' }),
    { enter: step('blur-in', 0, 800) },
  );
  b.text(
    s3,
    'end_tagline',
    c.tagline,
    b.in(0, 0.52, 1, 0.06),
    mono(b.s(0.022), `${p.ink}CC`, { align: 'center' }),
    { enter: step('fade', 600, 600) },
  );
  b.chip(
    s3,
    'cta',
    c.cta,
    { x: b.cx, y: b.F.y + b.F.h * 0.64, h: b.s(0.08), align: 'center' },
    { bg: p.accent, color: p.bg, font: p.font, weight: p.weight, size: b.s(0.032) },
    { enter: step('pop', 1000, 500) },
  );
  b.addFx(s3, 'light-leak', 0, s3.duration, {
    count: 2,
    color: p.accent,
    color2: p.pop,
    color3: p.ink,
    opacity: 0.35,
  });
  return solid(p.bg);
}

// ---------------------------------------------------------------- registry

export interface ReelStyle {
  scenes: (c: ReelCopy) => SceneSpec[];
  build: (b: VideoBuilder, c: ReelCopy, p: ReelPalette) => Fill | null;
  /** For the AI catalog. */
  about: string;
}

export const REEL_STYLES: Record<ReelStyleId, ReelStyle> = {
  launch: {
    scenes: launchScenes,
    build: launchBuild,
    about:
      'Product or app launch film: viewfinder HUD, a terminal typing the prompt, the logo, kinetic words flashing on colour flips, a sliced title, a code editor typing, the product word in focus brackets, call to action. ~18 s.',
  },
  showreel: {
    scenes: showreelScenes,
    build: showreelBuild,
    about:
      'Motion showreel / agency or personal brand: orbiting pills, the headline in focus brackets, a 3D dot floor, a particle sphere, a striped ribbon, rings of topics, the name in a serif. ~18 s.',
  },
  manifesto: {
    scenes: manifestoScenes,
    build: manifestoBuild,
    about:
      'Kinetic manifesto / bold statement: words slam one per beat with colour flips, a word wall behind the extruded 3D headline, topics stacking up, brand with a spinning sticker. ~11 s.',
  },
  data: {
    scenes: dataScenes,
    build: dataBuild,
    about:
      'Data story / results / explainer: the hook, a big counting number with a progress ring, bar and line charts, a split-flap board, call to action over a ticker tape. ~13 s.',
  },
  brand: {
    scenes: brandScenes,
    build: brandBuild,
    about:
      'Cinematic brand film on a photo: tracked-out brand name with light leaks, the headline over the picture, words blurring in, an end card. ~11 s. Uses the photo.',
  },
};

/** Content of a gallery reel in every language. */
export interface ReelCopyTx {
  brand: Tx | string;
  hud: Tx | string;
  prompt: Tx | string;
  words: Array<Tx | string>;
  headline: Tx | string;
  tagline: Tx | string;
  cta: Tx | string;
  topics: Array<Tx | string>;
  stat: { value: number; prefix?: string; suffix?: string; label: Tx | string };
  code: string[];
  photo?: PhotoId;
}

export function reelCopyIn(c: ReelCopyTx, b: Pick<VideoBuilder, 'tx'>): ReelCopy {
  const tx = (v: Tx | string) => b.tx(v);
  return {
    brand: tx(c.brand),
    hud: tx(c.hud),
    prompt: tx(c.prompt),
    words: c.words.map(tx),
    headline: tx(c.headline),
    tagline: tx(c.tagline),
    cta: tx(c.cta),
    topics: c.topics.map(tx),
    stat: { ...c.stat, label: tx(c.stat.label) },
    code: c.code,
    photo: c.photo,
  };
}
